import type {
  AppointmentRequest,
  AppointmentStatus,
  CompleteAppointmentInput,
  ProposeSlotsInput,
  RequestAppointmentInput,
  RespondAppointmentInput,
} from '@bcl/shared';
import { api } from '@/lib/api';

export type EnrichedAppointment = AppointmentRequest & {
  projectReference: string | null;
};

export type ClientFiche = {
  reference: string;
  client: { firstName: string; lastName: string; email: string; phone: string };
  info: unknown;
};

export type AssignedAppointment = EnrichedAppointment & {
  fiche: ClientFiche | null;
};

// --- Client ---

export async function requestAppointment(
  input: RequestAppointmentInput,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>('/appointments/request', input);
  return data;
}

export async function listMyAppointments(): Promise<EnrichedAppointment[]> {
  const { data } = await api.get<EnrichedAppointment[]>('/appointments/mine');
  return data;
}

export async function chooseAppointmentSlot(
  appointmentId: string,
  slotIndex: number,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>(
    `/appointments/${appointmentId}/choose-slot`,
    { slotIndex },
  );
  return data;
}

export async function rejectAppointmentSlots(
  appointmentId: string,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>(
    `/appointments/${appointmentId}/reject-slots`,
  );
  return data;
}

// --- Confirmation publique ---

export type AppointmentByToken = {
  proposedSlots: string[];
  status: AppointmentStatus;
  projectReference: string | null;
};

export async function getAppointmentByToken(
  token: string,
): Promise<AppointmentByToken> {
  const { data } = await api.get<AppointmentByToken>(
    `/appointments/by-token/${token}`,
  );
  return data;
}

export async function confirmSlot(
  token: string,
  slotIndex: number,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>('/appointments/confirm', {
    token,
    slotIndex,
  });
  return data;
}

// --- Destinataire (technicien / partenaire) ---

export async function listAssignedAppointments(): Promise<AssignedAppointment[]> {
  const { data } = await api.get<AssignedAppointment[]>('/appointments/assigned');
  return data;
}

export async function respondAppointment(
  appointmentId: string,
  input: RespondAppointmentInput,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>(
    `/appointments/${appointmentId}/respond`,
    input,
  );
  return data;
}

export async function completeAppointment(
  appointmentId: string,
  input: CompleteAppointmentInput,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>(
    `/appointments/${appointmentId}/complete`,
    input,
  );
  return data;
}

// --- Interventions & planning (planning.manage) ---

export type Intervention = {
  id: string;
  projectId: string;
  projectReference: string | null;
  clientName: string | null;
  scheduledAt: string | null;
  status: AppointmentStatus;
  executorKind: 'bcl' | 'partenaire' | null;
  executorName: string | null;
};

export async function listInterventions(): Promise<Intervention[]> {
  const { data } = await api.get<Intervention[]>('/appointments/interventions');
  return data;
}

// --- Back-office (planning.manage) ---

export async function listAllAppointments(
  status?: AppointmentStatus,
): Promise<EnrichedAppointment[]> {
  const { data } = await api.get<EnrichedAppointment[]>('/appointments', {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function proposeSlots(
  appointmentId: string,
  input: ProposeSlotsInput,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>(
    `/appointments/${appointmentId}/propose`,
    input,
  );
  return data;
}

// Rendez-vous piloté depuis la fiche dossier.
export async function getProjectAppointment(
  projectId: string,
): Promise<EnrichedAppointment | null> {
  const { data } = await api.get<EnrichedAppointment | null>(
    `/appointments/by-project/${projectId}`,
  );
  return data;
}

export async function proposeSlotsForProject(
  projectId: string,
  input: ProposeSlotsInput,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>(
    `/appointments/by-project/${projectId}/propose`,
    input,
  );
  return data;
}

export async function assignAppointment(
  appointmentId: string,
  assigneeId: string,
): Promise<AppointmentRequest> {
  const { data } = await api.post<AppointmentRequest>(
    `/appointments/${appointmentId}/assign`,
    { assigneeId },
  );
  return data;
}
